/**
 * Ripartisce il prezzo di vendita del preventivo sulle sue righe.
 *
 * Il calcolo del prezzo resta UNO SOLO e aggregato: questa funzione non
 * ricalcola nulla, prende il `SellingBreakdown` gia` prodotto da `applyPricing`
 * e lo scompone. Per costruzione la somma dei ricavi di riga e` esattamente
 * `grossPrice`, cioe` il totale che i due PDF stampano gia` oggi nel piede.
 *
 * Usa i coefficienti EFFETTIVI del breakdown, non le percentuali di
 * configurazione: cosi` continua a tornare anche quando entra in gioco il ramo
 * a subtotale forzato di `applyPricing` (le righe con `markup_pct` proprio).
 *
 *   f_categoria = 1 + markup_categoria / costo_categoria
 *   f_accessori = grossPrice / (costBase + markupTotal)
 *
 * NOTA sullo sconto cliente: si ripartisce il LORDO, non il netto. Con uno
 * sconto diverso da zero i margini di riga sono quindi al lordo dello sconto,
 * mentre la card "Vista vendita" calcola il proprio margine sul netto. Oggi su
 * Drycore lo sconto e` 0 e le due letture coincidono.
 */
import type { SellingBreakdown } from './applyPricing';
import type { EstimateRowCost } from './computeEstimateCostBase';

export interface RowRevenue {
  id?: string;
  area: number;
  /** Costo della riga (€), difficolta` e perimetro inclusi. */
  cost: number;
  /** Ricavo attribuito ai materiali, profilo perimetrale incluso (€). */
  revenueMaterials: number;
  /** Ricavo attribuito alla manodopera, maggiorazione difficolta` inclusa (€). */
  revenueLabor: number;
  /** Ricavo attribuito alla finitura (€). */
  revenueFinish: number;
  /** Ricavo totale della riga (€). */
  revenueTotal: number;
  /** Prezzo di vendita al m² della riga (€/m²). */
  revenuePerSqm: number;
  /** Margine in euro. */
  margin: number;
  /**
   * Margine in percentuale SUL RICAVO, non sul costo.
   *
   * Prima era `margine / costo` e dava 37,5% dove il margine sul ricavo era il
   * 27,3%: due definizioni diverse nella stessa schermata, visto che la card
   * "Vista vendita" usa il ricavo. E` il margine LORDO — il netto della card
   * toglie anche i costi accessori.
   */
  marginPct: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function distributeSellingPrice(
  rows: readonly EstimateRowCost[] | null | undefined,
  breakdown: SellingBreakdown,
): RowRevenue[] {
  const fMat = breakdown.costMaterials > 0
    ? 1 + breakdown.markupMaterials / breakdown.costMaterials : 1;
  const fLab = breakdown.costLabor > 0
    ? 1 + breakdown.markupLabor / breakdown.costLabor : 1;
  const fFin = breakdown.costFinish > 0
    ? 1 + breakdown.markupFinish / breakdown.costFinish : 1;
  const subtotal = breakdown.costBase + breakdown.markupTotal;
  const fAcc = subtotal > 0 ? breakdown.grossPrice / subtotal : 1;

  const out: RowRevenue[] = (rows ?? []).map((r) => {
    const revenueMaterials = r.materials * fMat * fAcc;
    const revenueLabor = r.labor * fLab * fAcc;
    const revenueFinish = r.finish * fFin * fAcc;
    const revenueTotal = revenueMaterials + revenueLabor + revenueFinish;
    return {
      id: r.id,
      area: r.area,
      cost: round2(r.total),
      revenueMaterials: round2(revenueMaterials),
      revenueLabor: round2(revenueLabor),
      revenueFinish: round2(revenueFinish),
      revenueTotal: round2(revenueTotal),
      revenuePerSqm: r.area > 0 ? revenueTotal / r.area : 0,
      margin: round2(revenueTotal - r.total),
      marginPct: revenueTotal > 0 ? ((revenueTotal - r.total) / revenueTotal) * 100 : 0,
    };
  });

  // I centesimi persi negli arrotondamenti finiscono sull'ultima riga, cosi`
  // la somma della colonna coincide al centesimo col totale del preventivo.
  if (out.length > 0) {
    const somma = out.reduce((s, r) => s + r.revenueTotal, 0);
    const delta = round2(breakdown.grossPrice - somma);
    if (delta !== 0) {
      const last = out[out.length - 1];
      last.revenueTotal = round2(last.revenueTotal + delta);
      last.revenueLabor = round2(last.revenueLabor + delta);
      last.margin = round2(last.revenueTotal - last.cost);
      last.revenuePerSqm = last.area > 0 ? last.revenueTotal / last.area : 0;
      last.marginPct = last.revenueTotal > 0 ? (last.margin / last.revenueTotal) * 100 : 0;
    }
  }
  return out;
}
